import React, {useEffect, useState} from 'react';
import cx from 'classnames';
import {
  useFormikContext, Formik, Form, Field,
} from 'formik';
import {OverlayTrigger, Tooltip} from 'react-bootstrap';

import {submitRubric} from '../../api/api';

import FlashMessage from '../../components/FlashMessage';
import Button from '../../components/Button';
import {Rubrics, Scale} from '../../constants/rubrics';
import SignupModal from '../../components/SignupModal';

import PromotionalListItem from '../QuestionListPage/PromotionalListItem';

import {useAnalytics, useAppContext} from '../../hooks';

const RubricForm = ({
  rubricInfo,
  error,
  hideCallback,
  submitted,
  showExpertReview,
  expertReview,
  lessonSlug,
}) => {
  const {tracker} = useAnalytics();
  const {dirty, values} = useFormikContext();
  const {
    currentUser, hasSubscription,
  } = useAppContext();
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (dirty && !touched) {
      tracker.lessonClickStartRubric({
        lesson: lessonSlug, currentUser: !!currentUser, hasSubscription,
      });
      setTouched(true);
    }
  }, [dirty]);

  return (
    <Form>
      <div className="flex flex-col xs:flex xs:items-center p-2 my-2">
        {rubricInfo.map((skill, i) => (
          <>
            {i === 0 && (
            <div className="hidden w-2/3 xs:flex flex-row text-base self-end">
              {Object.keys(skill.scale).map((sc) => (
                <div className="mb-2 flex-1 text-center">
                  {sc}
                </div>
              ))}
            </div>
            )}
            <div className="w-full my-1 flex flex-col xs:flex-row align-center">
              <div className="xs:w-1/3 text-base font-semibold">{skill.name}</div>
              <div className="flex flex-col m-2 xs:m-0 xs:flex-row flex-grow justify-evenly">
                {Object.keys(skill.scale).map((sc) => (
                  <OverlayTrigger
                    key={`${skill.name}_${sc}`}
                    placement="top"
                    delay={{show: 300, hide: 400}}
                    overlay={(<Tooltip id={`${skill.name}_${sc}_tooltip`}>{skill.scale[sc]}</Tooltip>)}
                  >
                    <div className="w-full flex flex-row xs:h-6 justify-center xs:rounded-full">
                      <label
                        htmlFor={`${skill.name}_${sc}`}
                        key={`${skill.name}_${sc}`}
                        className="block xs:hidden text-base font-normal"
                      >
                        {sc}
                      </label>
                      <Field
                        id={`${skill.name}_${sc}`}
                        name={skill.name}
                        type="radio"
                        checked={values[skill.name] === sc}
                        className={cx(`form-radio
                              m-0
                              focus:ring-indigo-500
                              h-4
                              w-4
                              border-gray-300`, {
                          'text-indigo-600': values[skill.name] === sc,
                          'ring-2 ring-offset-2 ring-green-500': showExpertReview
                                && submitted
                                && Scale[expertReview[skill.name]] === sc,
                        })}
                        disabled={submitted}
                        value={sc}
                      />
                    </div>
                  </OverlayTrigger>
                ))}
              </div>
            </div>
          </>
        ))}
      </div>
      <div className={cx('flex flex-col xs:flex-row py-2 justify-evenly xs:justify-end', {
        hidden: submitted,
      })}
      >
        <div className={cx('px-4', {
          hidden: !error.status,
        })}
        >
          <FlashMessage
            show={error.status}
            hideCallback={hideCallback}
            duration={2000}
          >
            <div className="text-red-400">
              {error.message}
            </div>
          </FlashMessage>
        </div>
        {!submitted && <Button type="submit">Submit to see expert review</Button>}
      </div>
    </Form>
  );
};

const InteractiveRubric = ({
  lesson, showPaywall, showSignupWall, trackSlug, lessonSlug, courseSlug,
}) => {
  const rubricType = lesson?.rubric?.rubricName;
  const rubricInfo = Rubrics?.[rubricType]?.skills;
  const rubricActivity = lesson?.rubricActivity?.[rubricType];
  const [submitted, setSubmitted] = useState(Boolean(rubricActivity));
  const [error, setError] = useState({status: false});
  const {tracker} = useAnalytics();
  const {
    currentUser, hasSubscription,
  } = useAppContext();

  const handleSubmit = async (values) => {
    try {
      tracker.lessonSubmitRubric({
        lesson: lessonSlug,
        currentUser: !!currentUser,
        hasSubscription,
        values,
      });
      if (showSignupWall || !currentUser) {
        setSubmitted(true);
        return;
      }
      await submitRubric(lessonSlug, {[rubricType]: values});
      setSubmitted(true);
    } catch (err) {
      setError({status: true, message: 'An error has occurred. Please try again later.'});
    }
  };

  const hideCallback = () => setError({status: false});

  const skills = rubricInfo.flatMap((s) => s.name) || [];
  const initialValues = rubricActivity || skills.reduce((obj, item) => ({...obj, [item]: 'Neutral'}), {});
  const expertReview = lesson?.rubric?.scores ?? {};
  const expertRecommendation = lesson?.rubric?.expertOpinion || '';
  const expertRecommendationText = lesson?.rubric?.expertOpinionText || '';
  const showExpertReview = hasSubscription && !!currentUser;

  return (
    <div className="border rounded-lg my-6 p-6">
      <div className="m-2 text-2xl font-semibold text-gray-900">Score the skills demonstrated in this interview:</div>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        <RubricForm
          rubricInfo={rubricInfo}
          hideCallback={hideCallback}
          error={error}
          submitted={submitted}
          showExpertReview={showExpertReview}
          expertReview={expertReview}
          lessonSlug={lessonSlug}
        />
      </Formik>
      {submitted && showExpertReview && (
        <div className="my-2 rounded-lg bg-indigo-100 text-base space-y-2">
          <div className="p-4 font-semibold">
            Exponent Coach Decision:
            {' '}
            {expertRecommendation}
          </div>
          <div className="px-4 pb-4">
            {expertRecommendationText}
          </div>
        </div>
      )}
      {submitted && !showExpertReview && (
      <PromotionalListItem
        title="Upgrade now to see an in-depth expert analysis"
        subtitle="Review insights into the overall hiring decision"
        btnText="Upgrade"
        href={`/upgrade?course=${courseSlug}&src=lesson`}
        onClick={() => tracker.lessonClickUpgrade({
          lesson: lessonSlug, course: courseSlug, track: trackSlug, rubricType,
        })}
      />
      )}
      <SignupModal source="lesson" isOpen={showSignupWall && submitted} />
    </div>
  );
};

export default InteractiveRubric;
